import { useCallback, useEffect, useState } from "react"

export interface Resource<T> {
  data: T | null
  loading: boolean
  error: Error | null
  reload: () => void
}

/**
 * Carrega um recurso assincrono de um service.
 *
 * Toda a leitura de dados do site passa por aqui — inclusive hoje, em que os
 * services leem JSON do proprio bundle. Isso e intencional: quando a fonte
 * virar a API da BusiPet, o que muda e o adapter, e nenhum componente precisa
 * ganhar estados de loading e erro que ainda nao tinha.
 *
 * `loader` precisa ser estavel (useCallback ou funcao de modulo). Uma arrow
 * recriada a cada render entra em laco infinito de fetch.
 */
export function useResource<T>(loader: () => Promise<T>): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    // Resposta de uma chamada anterior nao pode sobrescrever a atual: o
    // usuario troca de filtro mais rapido do que a rede responde.
    let cancelled = false

    setLoading(true)
    setError(null)

    loader()
      .then((result) => {
        if (cancelled) return
        setData(result)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause instanceof Error ? cause : new Error(String(cause)))
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [loader, nonce])

  return { data, loading, error, reload }
}
